import type { ColumnsType } from "antd/es/table";
import {
  Empty,
  Select,
  Button,
  Form,
  Input,
  Divider,
  Checkbox,
  Table,
  Tag,
  Spin,
  FormInstance,
  InputRef,
  TablePaginationConfig,
  Tooltip,
  TableProps,
} from "antd";
import "./Mapping.css";
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  FilterFilled,
  CaretUpOutlined, CaretDownOutlined
} from "@ant-design/icons";
import { useContext, useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import styles from "./Mapping.module.css";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setLegacyMappingCommentsFilter, setLegacyMappingFromFieldFilter, setLegacyMappingToFieldFilter, setRecordsPerPage } from "../../../../../redux/actions";
import Search from "antd/lib/input/Search";
import ApplicationService from "../../../../../services/application/ApplicationService";
import FieldMappingsService from "../../../../../services/fieldmappings/FieldMappingsService";
import { TABLE_SETUP_HIDE_SYSTEM_FIELDS } from "../../../../../config/consts/appsettings";
import { Table as TableType } from "../../../../../models/tables/Table";


interface UnmappedDataType {
  key: string;
  name: string;
  label: string;
  description: string;
}

interface DraggableBodyRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
}

interface DroppableBodyRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  index: number;
  mapRow: (dragIndex: number, hoverIndex: number) => void;
}

export interface TableDataType {
  key: string;
  id: number;
  fromValue: string;
  toField: string;
  label: string;
  description: string;
  mandatory: boolean;
  comments: string;
  fromTableFields: any;
  fromTableId: number | null;
  fromFieldId: string | null;
  fromField: string | null;
  toTableId: number;
  toFieldId: number;
  isSystemField: boolean;
  isAiField: boolean;
}

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
  key: string;
  fromValue: string;
  toField: string;
  comments: string;
  fromFieldId: string | null;
  fromField: string | null;
}

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

type EditableTableProps = Parameters<typeof Table>[0];

type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

type OnChange = NonNullable<TableProps<UnmappedDataType>['onChange']>;

type GetSingle<T> = T extends (infer U)[] ? U : never;
type Sorts = GetSingle<Parameters<OnChange>[2]>;

const type = "DraggableBodyRow";

const Mapping = ({
  sourceTables,
  setSelectedSourceTable,
  setFieldMappings,
  setIsTableLoading,
  unmappedFields,
  setUnmappedFields,
  sourceTableFields,
  fieldMappings,
  editableFieldMappings,
  setEditableFieldMappings,
  isTableLoading,
  selectedEntity,
  selectedGroupCode,
  selectedSourceTable,
  selectedDestinationTable,
  setTriggerUpdate,
}: {
  sourceTables: TableType[];
  setSelectedSourceTable: React.Dispatch<React.SetStateAction<TableType | undefined>>;
  setFieldMappings: any;
  setIsTableLoading: any;
  unmappedFields: any;
  setUnmappedFields: any;
  sourceTableFields: any;
  fieldMappings: any;
  editableFieldMappings: TableDataType[];
  setEditableFieldMappings: any;
  isTableLoading: any;
  selectedEntity: any;
  selectedGroupCode: any;
  selectedSourceTable: TableType;
  selectedDestinationTable: TableType;
  setTriggerUpdate: any;
}) => {
  const { t, i18n } = useTranslation("translations");

  const { recordsPerPage } = useSelector((state: any) => ({
    recordsPerPage: state.recordsPerPage,
  }));

  const dispatch = useDispatch();

  const [showUnmappedOnly, setShowUnmappedOnly] = useState<boolean>(false);
  const [showMandatoryOnly, setShowMandatoryOnly] = useState<boolean>(false);

  const [isSaveDisabled, setIsSaveDisabled] = useState<any>(false);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(
    recordsPerPage ?? 20
  );

  const sourceTableRef = useRef<any>();

  const [applyFieldsSearchInput, setApplyFieldsSearchInput] = useState<string>("");
  const [filteredFieldsData, setFilteredFieldsData] = useState<any>();
  const [sortedInfo, setSortedInfo] = useState<Sorts>({});
  const [mappingSortedInfo, setMappingSortedInfo] = useState<Sorts>({});

  const [applyFromFieldSearchInput, setApplyFromFieldSearchInput] = useState<string>("");
  const [applyToFieldSearchInput, setApplyToFieldSearchInput] = useState<string>("");
  const [applyCommentSearchInput, setApplyCommentSearchInput] = useState<string>("");
  const [filteredEditableFieldMappings, setFilteredEditableFieldMappings] = useState<TableDataType[]>([]);
  const [totalFields, setTotalFields] = useState(0);
  const [totalMappedFields, setTotalMappedFields] = useState(0);

  const [showSystemFields, setShowSystemFields] = useState<boolean>(false);
  const [fromTableCounts, setFromTableCounts] = useState([]);



  const { legacyMappingFromFieldFilter, legacyMappingToFieldFilter, legacyMappingCommentsFilter } = useSelector((state: any) => ({
    legacyMappingFromFieldFilter: state.legacyMappingFromFieldFilter,
    legacyMappingToFieldFilter: state.legacyMappingToFieldFilter,
    legacyMappingCommentsFilter: state.legacyMappingCommentsFilter,
  }));

  let locale = {
    emptyText: [
      <Empty
        description={t("General.Labels.NoData", {
          lngs: i18n.languages,
          defaultValue: "No data to display",
        })}
        image={Empty.PRESENTED_IMAGE_DEFAULT}
      />,
    ],
  };

  useEffect(() => {
    ApplicationService.getPreferences().then((response: any) => {
      var hideSystemFieldsPreference = response.data.find(
        (p) =>
          p.key === TABLE_SETUP_HIDE_SYSTEM_FIELDS &&
          p.object === "Application"
      );
      if (hideSystemFieldsPreference) {
        setShowSystemFields(hideSystemFieldsPreference.value !== "true");
      }
    });
  }, []);

  useEffect(() => {

    if (selectedGroupCode && selectedEntity) {
      FieldMappingsService.readAll(selectedGroupCode, selectedEntity)
        .then((response: any) => {
          const fromTableCounts = response.data;
          setFromTableCounts(fromTableCounts);
        })
        .catch((error: any) => {
          console.error("Error fetching unmapped counts:", error);
        });
    }


  }, [selectedGroupCode, selectedEntity]);


  useEffect(() => {
    setFilteredFieldsData(sourceTableFields);
  }, [sourceTableFields])

  const removeFromField = (element: any) => {

    const newData = [...editableFieldMappings];
    const index = newData.findIndex(
      (item) =>
        element.toFieldId === item.toFieldId && element.toTableId === item.toTableId
    );

    newData[index].fromTableFields.splice(0, 1);
    newData[index].fromFieldId = null;
    newData[index].fromField = "";

    setEditableFieldMappings(newData);
  };

  const handleFieldsDataTableChange: OnChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter as Sorts);
  };

  const handleSort = (order: 'ascend' | 'descend' | null, key: string) => {
    let newOrder = order;
    if (mappingSortedInfo.order === order) {
      newOrder = null; // Revert to initial state
    }
    setMappingSortedInfo({ order: newOrder, columnKey: key });

    if (newOrder) {
      const sortedData = [...filteredEditableFieldMappings].sort((a, b) => {
        let aKey = a[key];
        let bKey = b[key];

        if (key == 'fromValue') {
          aKey = a[key] && a[key] !== "" ? a[key] : (a.fromField ?? "NOT MAPPED");
          bKey = b[key] && b[key] !== "" ? b[key] : (b.fromField ?? "NOT MAPPED");
        }

        return newOrder === 'ascend'
          ? aKey.trim().localeCompare(bKey.trim())
          : bKey.trim().localeCompare(aKey.trim());
      });
      setFilteredEditableFieldMappings(sortedData);
    } else {
      setFilteredEditableFieldMappings([...editableFieldMappings]); // Revert to initial state
    }
  };

  const columns: ColumnsType<UnmappedDataType> = [
    {
      title: t(
        "Page.Operation.LegacyMapping.Tabs.Mapping.SideTable.Headers.Fields",
        {
          lngs: i18n.languages,
          defaultValue: "Fields",
        }
      ),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
      render: (value, record, index) => {
        return (
          <div className={styles.tableRow}>
            {record.label ? record.name + " | " + record.label : record.name}
            {editableFieldMappings.findIndex((x: any) => x.fromFieldId === record.key) !== -1 && (
              <Tooltip
                placement="right"
                title={t(
                  "Page.Operation.LegacyMapping.Tabs.Mapping.SideTable.Headers.FieldAlreadyMapped",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Already mapped",
                  }
                )}
              >
                <ExclamationCircleOutlined style={{ paddingTop: 5 }} />
              </Tooltip>
            )}
          </div>
        )
      },
    },
  ];

  const columnsTable: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
      {
        title: () => {
          return (
            <>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 14, fontWeight: "bold", display: "flex", alignItems: "center" }}>
                  {t(
                    "Page.Operation.LegacyMapping.Tabs.Mapping.Table.Headers.FromField",
                    {
                      lngs: i18n.languages,
                      defaultValue: "From field",
                    }
                  )}
                </span>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span className="ant-table-column-sorter">
                    <span className="ant-table-column-sorter-inner"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort(mappingSortedInfo.columnKey == 'fromValue' ? (mappingSortedInfo.order == 'ascend' ? 'descend' : mappingSortedInfo.order == 'descend' ? null : 'ascend') : 'ascend', 'fromValue')}>
                      <span
                        style={{ width: '20px', right: '0' }}
                        role="button"
                        className={`ant-table-column-sorter-up ${mappingSortedInfo.columnKey == 'fromValue' && mappingSortedInfo.order === 'ascend' ? 'active' : ''}`}

                      >
                        <CaretUpOutlined />
                      </span>
                      <span
                        style={{ width: '20px', right: '0' }}
                        role="button"
                        className={`ant-table-column-sorter-down ${mappingSortedInfo.columnKey == 'fromValue' && mappingSortedInfo.order === 'descend' ? 'active' : ''}`}
                      >
                        <CaretDownOutlined />
                      </span>
                    </span>
                  </span>
                  {!legacyMappingFromFieldFilter && (
                    <FilterOutlined style={{ paddingTop: 4 }} onClick={() => dispatch(setLegacyMappingFromFieldFilter(!legacyMappingFromFieldFilter))} />
                  )}
                  {legacyMappingFromFieldFilter && (
                    <FilterFilled style={{ paddingTop: 4 }} onClick={() => dispatch(setLegacyMappingFromFieldFilter(!legacyMappingFromFieldFilter))} />
                  )}
                </div>
              </div>
              {legacyMappingFromFieldFilter && (
                <Search
                  placeholder={
                    t(
                      "Page.Operation.LegacyMapping.Tabs.Mapping.Placeholders.Search",
                      {
                        lngs: i18n.languages,
                        defaultValue: "Search",
                      }
                    )!
                  }
                  value={applyFromFieldSearchInput}
                  onChange={(e) => setApplyFromFieldSearchInput(e.target.value)}
                />
              )}
            </>
          );
        },
        dataIndex: "fromValue",
        key: "fromValue",
        editable: true,
        render: (text, row: any) => {
          if (!row.fromField && !row.fromValue) {
            return (
              <Tag color="red" key={row.key}>
                {t(
                  "Page.Operation.LegacyMapping.Tabs.Mappings.Table.Data.NotMapped",
                  {
                    lngs: i18n.languages,
                    defaultValue: "NOT MAPPED",
                  }
                )}
              </Tag>
            );
          } else {
            if (row.fromField) {
              return (
                <div className={styles.tableRow}>
                  {row.fromField}
                  {
                    <Button
                      icon={<CloseOutlined />}
                      type="ghost"
                      size="small"
                      className={styles.rowRemoveMappingIcon}
                      onClick={() => {
                        removeFromField(row);
                      }}
                    ></Button>
                  }
                </div>
              );
            }
            if (row.fromValue && row.fromValue !== "") {
              return <div>{row.fromValue}</div>;
            }
            return text;
          }
        },
        shouldCellUpdate: (record, prevRecord) => true,
      },
      {
        title: () => {
          return (
            <>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 14, fontWeight: "bold", display: "flex", alignItems: "center" }}>
                  {t(
                    "Page.Operation.LegacyMapping.Tabs.Mapping.Table.Headers.ToField",
                    {
                      lngs: i18n.languages,
                      defaultValue: "To field",
                    }
                  )}
                </span>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span className="ant-table-column-sorter">
                    <span className="ant-table-column-sorter-inner"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort(mappingSortedInfo.columnKey == 'toField' ? (mappingSortedInfo.order == 'ascend' ? 'descend' : mappingSortedInfo.order == 'descend' ? null : 'ascend') : 'ascend', 'toField')}>
                      <span
                        style={{ width: '20px', right: '0' }}
                        role="button"
                        className={`ant-table-column-sorter-up ${mappingSortedInfo.columnKey == 'toField' && mappingSortedInfo.order === 'ascend' ? 'active' : ''}`}
                      >
                        <CaretUpOutlined />
                      </span>
                      <span
                        style={{ width: '20px', right: '0' }}
                        role="button"
                        className={`ant-table-column-sorter-down ${mappingSortedInfo.columnKey == 'toField' && mappingSortedInfo.order === 'descend' ? 'active' : ''}`}
                      >
                        <CaretDownOutlined />
                      </span>
                    </span>
                  </span>
                  {!legacyMappingToFieldFilter && (
                    <FilterOutlined style={{ paddingTop: 4 }} onClick={() => dispatch(setLegacyMappingToFieldFilter(!legacyMappingToFieldFilter))} />
                  )}
                  {legacyMappingToFieldFilter && (
                    <FilterFilled style={{ paddingTop: 4 }} onClick={() => dispatch(setLegacyMappingToFieldFilter(!legacyMappingToFieldFilter))} />
                  )}
                </div>
              </div>
              {legacyMappingToFieldFilter && (
                <Search
                  placeholder={
                    t(
                      "Page.Operation.LegacyMapping.Tabs.Mapping.Placeholders.Search",
                      {
                        lngs: i18n.languages,
                        defaultValue: "Search",
                      }
                    )!
                  }
                  value={applyToFieldSearchInput}
                  onChange={(e) => setApplyToFieldSearchInput(e.target.value)}
                />
              )}
            </>
          );
        },
        dataIndex: "toField",
        key: "toField",
        render: (text, row: any) => {
          if (row.mandatory) {
            return (
              <Tooltip
                placement="right"
                title={row.description}
              >
                <span className={styles.tableRowFieldRequired}>{row.label ? row.toField + " | " + row.label : row.toField}</span>
              </Tooltip>)
          } else {
            return (<Tooltip
              placement="right"
              title={row.description}
            >
              <span>{row.label ? row.toField + " | " + row.label : row.toField}</span>
            </Tooltip>)
          }
        },
      },
      {
        title: () => {
          return (
            <>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 14, fontWeight: "bold" }}>
                  {t(
                    "Page.Operation.LegacyMapping.Tabs.Mapping.Table.Headers.Comments",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Comments",
                    }
                  )}
                </span>
                {!legacyMappingCommentsFilter && (
                  <FilterOutlined style={{ paddingTop: 4 }} onClick={() => dispatch(setLegacyMappingCommentsFilter(!legacyMappingCommentsFilter))} />
                )}
                {legacyMappingCommentsFilter && (
                  <FilterFilled style={{ paddingTop: 4 }} onClick={() => dispatch(setLegacyMappingCommentsFilter(!legacyMappingCommentsFilter))} />
                )}
              </div>
              {legacyMappingCommentsFilter && (
                <Search
                  placeholder={
                    t(
                      "Page.Operation.LegacyMapping.Tabs.Mapping.Placeholders.Search",
                      {
                        lngs: i18n.languages,
                        defaultValue: "Search",
                      }
                    )!
                  }
                  value={applyCommentSearchInput}
                  onChange={(e) => setApplyCommentSearchInput(e.target.value)}
                />
              )}
            </>
          );
        },
        dataIndex: "comments",
        key: "comments",
        editable: true,
        render: (text, row: any) => {
          if (text?.trimStart().trimEnd().length === 0) {
            return <div style={{ opacity: 0 }}>/</div>;
          }
          return text;
        },
        shouldCellUpdate: (record, prevRecord) => true,
      },
    ];

  const EditableCell: React.FC<EditableCellProps> = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    ...restProps
  }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<InputRef>(null);
    const inputCursorPosition = 0;
    const form = useContext(EditableContext)!;

    useEffect(() => {
      if (editing) {
        inputRef.current!.focus();
      }
    }, [editing]);

    const toggleEdit = () => {
      setEditing(!editing);
      form.setFieldsValue({ [dataIndex]: record[dataIndex] });
    };

    const save = async () => {
      try {
        const values = await form.validateFields();
        if (values.fromValue !== undefined) {
          record.fromValue = values.fromValue;
          handleSave({ ...record, ...values });
        }
        if (values.comments !== undefined) {
          record.comments = values.comments;
          handleSave({ ...record, ...values });
        }
      } catch (errInfo) {
        console.log("Save failed:", errInfo);
      }
    };

    let childNode = children;

    if (editable && (dataIndex === 'comments' || (!record.fromFieldId && !record.fromField))) {
      childNode = editing ? (
        <Form.Item name={dataIndex}>
          <Input
            ref={inputRef}
            onBlur={() => {
              save();
              inputRef.current?.setSelectionRange(
                inputCursorPosition,
                inputCursorPosition
              );
            }}
          />
        </Form.Item>
      ) : (
        <div className="editable-cell-value-wrap" onClick={toggleEdit}>
          {children}
        </div>
      );
    }

    return <td {...restProps}>{childNode}</td>;
  };

  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };
  const tableItemsComponents = {
    body: {
      row: DroppableBodyRow,
      cell: EditableCell,
    },
  };

  const handleSave = (row: TableDataType) => {
    const newData = [...editableFieldMappings];
    const index = newData.findIndex(
      (item) => row.toFieldId === item.toFieldId && row.toTableId === item.toTableId
    );
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });

    // Update the unmapped count
    const tableIndex = fromTableCounts.findIndex(
      (count: any) => count.fromTableId === row.fromTableId
    );
    if (tableIndex !== -1) {
      const updatedCounts = [...fromTableCounts];
      if (row.mandatory && (row.fromFieldId || row.fromField)) {
        updatedCounts[tableIndex].unmappedCount -= 1;
      } else if (!row.mandatory && (!row.fromFieldId && !row.fromField)) {
        updatedCounts[tableIndex].unmappedCount += 1;
      }
      setFromTableCounts(updatedCounts);
    }

    setEditableFieldMappings(newData);
  };

  const editableColumns = columnsTable.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: TableDataType) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const onApplyFiledsSearch = (value: string) => {
    setFilteredFieldsData(sourceTableFields.filter(p =>
      p.name.toLowerCase().indexOf(value.toLowerCase()) > -1));

    setApplyFieldsSearchInput(value);
  };

  useEffect(() => {
    setFilteredEditableFieldMappings(editableFieldMappings);
  }, [editableFieldMappings]);

  useEffect(() => {
    let filteredMappings = editableFieldMappings
      .filter(mapping => mapping.fromField ? mapping.fromField.toLowerCase().indexOf(applyFromFieldSearchInput.toLowerCase()) > -1 :
        mapping.fromValue.toLowerCase().indexOf(applyFromFieldSearchInput.toLowerCase()) > -1)
      .filter(mapping => mapping.toField.toLowerCase().indexOf(applyToFieldSearchInput.toLowerCase()) > -1)
      .filter(mapping => mapping.comments.toLowerCase().indexOf(applyCommentSearchInput.toLowerCase()) > -1);

    if (showUnmappedOnly) {
      filteredMappings = filteredMappings.filter((el: any) => el.fromFieldId == null && !el.fromValue);
    }

    if (showMandatoryOnly) {
      filteredMappings = filteredMappings.filter((el: any) => el.mandatory);
    }

    if (!showSystemFields) {
      filteredMappings = filteredMappings.filter(
        (x: any) => !x.isSystemField
      );
    }

    // Reapply sorting only if a sorting order is specified
    if (mappingSortedInfo.columnKey && mappingSortedInfo.order) {
      const sortedData = [...filteredMappings].sort((a, b) => {
        let aColumn = a[mappingSortedInfo.columnKey];
        let bColumn = b[mappingSortedInfo.columnKey];

        if (mappingSortedInfo.columnKey == 'fromValue') {
          aColumn = a.fromValue && a.fromValue !== "" ? a.fromValue : (a.fromField ?? "NOT MAPPED");
          bColumn = b.fromValue && b.fromValue !== "" ? b.fromValue : (b.fromField ?? "NOT MAPPED");
        }
        return mappingSortedInfo.order === 'ascend'
          ? aColumn.trim().localeCompare(bColumn.trim())
          : bColumn.trim().localeCompare(aColumn.trim());
      });
      setFilteredEditableFieldMappings(sortedData);
      return;
    }

    setFilteredEditableFieldMappings(filteredMappings);
  }, [editableFieldMappings, applyFromFieldSearchInput, applyToFieldSearchInput, applyCommentSearchInput, showUnmappedOnly, showMandatoryOnly, showSystemFields]);

  const handleSourceChange = (value: number) => {
    setApplyFieldsSearchInput("");
    setIsTableLoading(true);

    setSelectedSourceTable(sourceTables.find((table: TableType) => table.id === value));
  };

  const optionsWithCounters = sourceTables.map((table: TableType) => {
    let unmappedCount = 0;
    if (table === selectedSourceTable) {
      const mandatoryFields = editableFieldMappings.filter(field =>
        field.mandatory &&
        (!field.fromValue && !field.fromField) &&
        field.fromTableId === table.id
      );

      unmappedCount = mandatoryFields.length;
    } else {
      unmappedCount = fromTableCounts.find((count: any) => count.fromTableId === table.id)?.unmappedCount || 0;
    }

    return {
      label: (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{table.name}</span>
          <span style={{ color: "#D3D3D3" }}>
            {unmappedCount > 0 ? `-${unmappedCount}` : "0"}
          </span>
        </div>
      ),
      value: table.id,
    };
  });

  const mapRow = (dragIndex: number, hoverIndex: number) => {
    const filteredIndex = hoverIndex + currentPageSize * (currentPageNumber - 1);
    const fieldMapping = filteredEditableFieldMappings[filteredIndex];
    const idx = editableFieldMappings.findIndex(m => m.toFieldId === fieldMapping.toFieldId);

    let fieldsData = filteredFieldsData;
    if (sortedInfo.order === "ascend") {
      fieldsData.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortedInfo.order === "descend") {
      fieldsData.sort((a, b) => b.name.localeCompare(a.name));
    }

    const dragRow = fieldsData[dragIndex];

    let newTableItems = editableFieldMappings.map((x: any) => {
      return { ...x };
    });
    newTableItems[idx].fromFieldId = dragRow.id;
    newTableItems[idx].fromField = dragRow.name;
    newTableItems[idx].fromTableFields = [
      {
        fromFieldId: dragRow.id,
        fromField: dragRow.name,
        tableId: dragRow.tableId,
      },
    ];
    setEditableFieldMappings([...newTableItems]);
  };

  const getMappingsDataSource = () => {
    let res = filteredEditableFieldMappings.map((x: TableDataType) => { return { ...x } });

    if (showUnmappedOnly) {
      res = res.filter(
        (el: any) => el.fromFieldId == null && !el.fromValue
      )
    }

    if (showMandatoryOnly) {
      res = res.filter(
        (el: any) => el.mandatory
      )
    }

    if (!showSystemFields) {
      res = res.filter(
        (x: any) => !x.isSystemField
      )
    }

    return res
  }

  const onPageChange = (page: any, pageSize: any) => {
    setCurrentPageNumber(page);
    setCurrentPageSize(pageSize);
    dispatch(setRecordsPerPage(pageSize));
  };

  const pagination: TablePaginationConfig = {
    defaultPageSize: currentPageSize,
    current: currentPageNumber,
    pageSize: currentPageSize,
    onChange: onPageChange,
    showQuickJumper: true,
    showSizeChanger: true,
    locale: {
      items_per_page: t("General.Table.Pagination.ItemPerPage", {
        lngs: i18n.languages,
        defaultValue: "/page",
      })!,
      page: t("General.Table.Pagination.Page", {
        lngs: i18n.languages,
        defaultValue: "page",
      })!,
      jump_to: t("General.Table.Pagination.GoTo", {
        lngs: i18n.languages,
        defaultValue: "Go to",
      })!,
    },
    showTotal: (total: number, range: [number, number]) => {
      const translation = t("General.Table.Pagination.RangeRecords", {
        lngs: i18n.languages,
        defaultValue: `${range[0]}-${range[1]} out of ${total} records`,
      });
      return `${translation
        .replace("${range[0]}", range[0].toString())
        .replace("${range[1]}", range[1].toString())
        .replace("${total}", total.toString())}`;
    },
    itemRender: (page: number, type, originalElement) => {
      return originalElement;
    },
  };

  const isFilterEnabled = () => {
    return legacyMappingFromFieldFilter || legacyMappingToFieldFilter || legacyMappingCommentsFilter;
  }

  const scroll = { y: isFilterEnabled() ? "calc(100vh - 420px)" : "calc(100vh - 388px)" };

  useEffect(() => {
    if (selectedGroupCode && selectedEntity) {
      sourceTableRef.current.focus();
      return;
    }
    sourceTableRef.current.blur();
  }, [selectedGroupCode, selectedEntity]);

  useEffect(() => {
    let newTableItems = fieldMappings.map((x: any) => {
      return { ...x };
    });
    setEditableFieldMappings([...newTableItems]);
  }, [fieldMappings]);

  useEffect(() => {
    setEditableFieldMappings(
      editableFieldMappings.sort(function (a: any, b: any) {
        return a.toPosition - b.toPosition;
      })
    );
  }, [editableFieldMappings]);

  useEffect(() => {
    if (selectedGroupCode && selectedEntity && sourceTables?.length === 1) {
      setSelectedSourceTable(sourceTables[0]);
      sourceTableRef.current.blur();
    }
  }, [selectedGroupCode, selectedEntity, sourceTables]);

  useEffect(() => {
    const totalFieldsCount = editableFieldMappings.filter(
      (field) => (field.fromFieldId !== null || field.fromField !== null) && !field.isSystemField
    ).length;

    const totalMappedFieldsCount = editableFieldMappings.filter(
      (field) => (field.fromValue || field.fromField) && !field.isSystemField
    ).length;

    setTotalFields(totalFieldsCount);
    setTotalMappedFields(totalMappedFieldsCount);
  }, [editableFieldMappings]);

  return (
    <div className="legacy-mapping-tab-container">
      <div className="section-container">
        <div>
          <div className="section-header">
            <Select
              ref={sourceTableRef}
              disabled={!selectedGroupCode || !selectedEntity}
              placeholder={t(
                "Page.Operation.LegacyMapping.Tabs.Mapping.Placeholders.SourceTable",
                {
                  lngs: i18n.languages,
                  defaultValue: "Source table",
                }
              )}
              allowClear
              style={{ width: "100%", marginBottom: "5px" }}
              dropdownMatchSelectWidth={false}
              value={selectedSourceTable ? selectedSourceTable.id : undefined}
              onChange={handleSourceChange}
              options={optionsWithCounters}
            />
          </div>
          <Search
            placeholder={
              t(
                "Page.Operation.LegacyMapping.Tabs.Mapping.Placeholders.Search",
                {
                  lngs: i18n.languages,
                  defaultValue: "Search",
                }
              )!
            }
            value={applyFieldsSearchInput}
            onChange={(e) => onApplyFiledsSearch(e.target.value)}
            disabled={!selectedSourceTable}
          />
          <Divider />
          <div className="section-items">
            {isTableLoading ? (
              <div className={styles.loadingContainer}>
                <Spin />
              </div>
            ) : (
              <>
                {selectedEntity && selectedGroupCode && selectedSourceTable ? (
                  <div className="legacy-mapping-table-container">
                    <Table
                      locale={locale}
                      dataSource={filteredFieldsData.map((x: any) => ({
                        key: x.id,
                        name: x.name,
                        label: x.label,
                        description: x.description,
                      }))}
                      pagination={false}
                      columns={columns}
                      components={components}
                      size="small"
                      onRow={(_, index) => {
                        const attr = {
                          index,
                          mapRow,
                        };
                        return attr as React.HTMLAttributes<any>;
                      }}
                      onChange={handleFieldsDataTableChange}
                      scroll={{ y: 'calc(100vh - 441px)' }}
                    />
                  </div>
                ) : (
                  <>
                    {!selectedGroupCode || !selectedEntity ? (
                      <Empty
                        description={t(
                          "Page.Operation.LegacyMapping.Tabs.Mapping.Tutorial.GetStarted",
                          {
                            lngs: i18n.languages,
                            defaultValue:
                              "Please select Group Code and Entity to get started",
                          }
                        )}
                      ></Empty>
                    ) : (
                      <Empty
                        description={t(
                          "Page.Operation.LegacyMapping.Tabs.Mapping.Tutorial.SelectSourceTable",
                          {
                            lngs: i18n.languages,
                            defaultValue:
                              "Please select a source table to continue",
                          }
                        )}
                      ></Empty>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="legacy-mapping-data-container">
        {isTableLoading ? (
          <div className={styles.loadingContainer}>
            <Spin />
          </div>
        ) : (
          <>
            {selectedSourceTable ? (
              <>
                <div className="title-actions">

                  <span style={{ color: "#D3D3D3" }}>
                    {t("Page.Operation.LegacyMapping.Tabs", {
                      lngs: i18n.languages,
                      defaultValue: `${totalMappedFields} field${totalMappedFields === 1 ? "" : "s"} mapped from ${totalFields} available.`,
                    })}
                  </span>
                  <div className="action-buttons">
                    <Checkbox
                      checked={showMandatoryOnly}
                      onClick={() => setShowMandatoryOnly(!showMandatoryOnly)}
                    >
                      {t(
                        "Page.Operation.LegacyMapping.Tabs.Mapping.Labels.ShowMandatoryOnly",
                        {
                          lngs: i18n.languages,
                          defaultValue: "Show mandatory only",
                        }
                      )}
                    </Checkbox>
                    <Checkbox
                      checked={showUnmappedOnly}
                      onClick={() => setShowUnmappedOnly(!showUnmappedOnly)}
                    >
                      {t(
                        "Page.Operation.LegacyMapping.Tabs.Mapping.Labels.ShowUnmappedOnly",
                        {
                          lngs: i18n.languages,
                          defaultValue: "Show unmapped only",
                        }
                      )}
                    </Checkbox>
                    <Checkbox
                      checked={showSystemFields}
                      onClick={() => setShowSystemFields(!showSystemFields)}
                    >
                      {t(
                        "Page.Operation.LegacyMapping.Tabs.Mapping.Labels.ShowSystemFields",
                        {
                          lngs: i18n.languages,
                          defaultValue: "Show system fields",
                        }
                      )}
                    </Checkbox>
                  </div>
                </div>
                <div
                  className="legacy-mapping-table-container"
                >
                  <Table
                    scroll={scroll}
                    locale={locale}
                    components={tableItemsComponents}
                    dataSource={getMappingsDataSource()}
                    columns={editableColumns as ColumnTypes}
                    pagination={false}
                    size="small"
                    onRow={(record, index) => {
                      const attr = {
                        index,
                        mapRow,
                      };
                      return attr as React.HTMLAttributes<any>;
                    }}
                    rowClassName={(record: TableDataType) => {

                      const classes: string[] = [];
                      if (!record.fromValue && !record.fromField) {
                        classes.push("row-color-grey");
                      }

                      if (record.isSystemField) {
                        classes.push("row-system-field");
                      }

                      if (record.isAiField) {
                        classes.push("row-ai");
                      }

                      return classes.join(" ");
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="empty-container">
                <Empty
                  description={
                    selectedSourceTable
                      ? t("General.Labels.NoData", {
                        lngs: i18n.languages,
                        defaultValue: "No data to display",
                      })
                      : ""
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const DraggableBodyRow = ({
  index,
  moveRow,
  className,
  style,
  ...restProps
}: DraggableBodyRowProps) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? " drop-over-downward" : " drop-over-upward",
      };
    },
    drop: (item: { index: number }) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ""}`}
      style={{ cursor: "move", ...style }}
      {...restProps}
    />
  );
};

const DroppableBodyRow: React.FC<DroppableBodyRowProps> = ({
  index,
  mapRow,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? " drop-over-downward" : " drop-over-upward",
      };
    },
    drop: (item: { index: number }) => {
      mapRow(item.index, index);
    },
  });

  drop(ref);

  const [form] = Form.useForm();

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr
          ref={ref}
          className={`${className}${isOver ? dropClassName : ""}`}
          style={{ ...style }}
          {...restProps}
        />
      </EditableContext.Provider>
    </Form>
  );
};

export default Mapping;
